import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Pencil, CheckSquare, X, Save, Package } from "lucide-react";
import { timeAgo } from "@/lib/time-ago";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface FreeItem {
  id: number;
  title: string;
  description: string;
  category: string;
  town: string;
  condition: string;
  contactName: string;
  contactPhone?: string | null;
  imageUrl?: string | null;
  collected: boolean;
  createdAt: string;
  userId?: number | null;
}

const CONDITION_STYLES: Record<string, string> = {
  "Like New": "bg-emerald-100 text-emerald-700",
  "Good": "bg-blue-100 text-blue-700",
  "Used": "bg-amber-100 text-amber-700",
  "Poor": "bg-red-100 text-red-700",
};

const CONDITIONS = ["Like New", "Good", "Used", "Poor"];

function EditModal({ item, onClose, onSaved }: { item: FreeItem; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    title: item.title,
    description: item.description,
    category: item.category,
    town: item.town,
    condition: item.condition,
    contactName: item.contactName,
    contactPhone: item.contactPhone ?? "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`/api/free-items/${item.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Item updated" });
      onSaved();
      onClose();
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Edit Free Item</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <form id="edit-form" onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Title</label>
              <input
                required
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Description</label>
              <textarea
                required
                rows={3}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary resize-none"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Town</label>
                <input
                  value={form.town}
                  onChange={e => setForm({ ...form, town: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Category</label>
                <input
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Condition</label>
              <div className="flex gap-2 flex-wrap">
                {CONDITIONS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, condition: c })}
                    className="px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors"
                    style={{
                      backgroundColor: form.condition === c ? "#1e3a5f" : "var(--color-card)",
                      borderColor: form.condition === c ? "#1e3a5f" : "#e2e8f0",
                      color: form.condition === c ? "var(--color-card)" : "#64748b",
                    }}
                  >{c}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Contact Name</label>
                <input
                  value={form.contactName}
                  onChange={e => setForm({ ...form, contactName: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Contact Phone</label>
                <input
                  value={form.contactPhone}
                  onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>
          </form>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            form="edit-form"
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFreeItems() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<FreeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "available" | "collected">("available");
  const [editItem, setEditItem] = useState<FreeItem | null>(null);
  const [busy, setBusy] = useState<Set<number>>(new Set());

  const isMod = (user as { isModerator?: boolean } | null)?.isModerator === true;
  if (authLoading) return null;
  if (!user || (user.role !== "admin" && !isMod)) return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/content/free-items", { headers: authHeaders() });
      const data = await r.json() as { items: FreeItem[] };
      setItems(data.items ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const markBusy = (id: number) => setBusy(p => new Set([...p, id]));
  const clearBusy = (id: number) => setBusy(p => { const s = new Set(p); s.delete(id); return s; });

  const handleDelete = async (item: FreeItem) => {
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    markBusy(item.id);
    try {
      const r = await fetch(`/api/free-items/${item.id}`, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Item deleted" });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally {
      clearBusy(item.id);
    }
  };

  const handleCollected = async (item: FreeItem) => {
    markBusy(item.id);
    try {
      const r = await fetch(`/api/free-items/${item.id}/collected`, { method: "PATCH", headers: authHeaders() });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Marked as collected" });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, collected: true } : i));
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally {
      clearBusy(item.id);
    }
  };

  const filtered = items.filter(i =>
    filter === "all" ? true : filter === "available" ? !i.collected : i.collected
  );

  return (
    <AdminLayout current="/admin/free-items">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Market</h1>
            <p className="text-sm text-slate-500 mt-0.5">{items.length} total · {items.filter(i => !i.collected).length} available</p>
          </div>
          <div className="flex gap-2">
            {(["all", "available", "collected"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all capitalize ${
                  filter === f ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600 hover:border-primary/40"
                }`}
              >{f}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Package size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No items to show</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => {
              const condStyle = CONDITION_STYLES[item.condition] ?? "bg-slate-100 text-slate-600";
              const isBusy = busy.has(item.id);
              return (
                <div key={item.id} className={`bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex gap-4 ${item.collected ? "opacity-60" : ""}`}>
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800 truncate">{item.title}</h3>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${condStyle}`}>{item.condition}</span>
                          {item.collected && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Collected</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          📍 {item.town} · {item.category} · 👤 {item.contactName}
                          {item.contactPhone && ` · 📞 ${item.contactPhone}`}
                          {" · "}{timeAgo(item.createdAt)}
                        </p>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-1">{item.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => setEditItem(item)}
                          disabled={isBusy}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-40"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        {!item.collected && (
                          <button
                            onClick={() => void handleCollected(item)}
                            disabled={isBusy}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
                            title="Mark collected"
                          >
                            <CheckSquare size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => void handleDelete(item)}
                          disabled={isBusy}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editItem && (
        <EditModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={load}
        />
      )}
    </AdminLayout>
  );
}
